// ═══════════════════════════════════════════════
//   LOGBOOK — V3 Detail split (list + entry)
//
//   Purpose:    Encyclopaedia of aliens, items, and future categories.
//   Opens from: Desktop icon / Files → APPS
//   Persisted:  S.logbookFavorites (array of alien ids)
//   Reads from: ALIENS, ALIEN_KEYS, GACHA_POOL, S.myAliens, S.myToys
// ═══════════════════════════════════════════════

const {S, ALIENS, ALIEN_KEYS, BUNNYMEALS, BUNNYMEAL_KEYS, LOGBOOK_CHARACTERS, CHARACTER_KEYS, CHARACTER_UNLOCK_EMAIL, LOGBOOK_OBJECTS, OBJECT_KEYS, GACHA_POOL, SFX, toast, scheduleSave, openWin} = window;

const TOY_CATALOGUE = GACHA_POOL.filter(p => p.type === 'toy');
const TOY_KEYS = TOY_CATALOGUE.map(t => t.id);
// Items tab catalogues both toys (collectibles) and story objects (dossier
// entries). ITEM_KEYS is the concatenated nav order used by logbookStep.
const ITEM_KEYS = [...TOY_KEYS, ...OBJECT_KEYS];

function _isObjectId(id) { return !!LOGBOOK_OBJECTS[id]; }
function _isObjectUnlocked(o) {
    if (!o) return false;
    if (!o.unlock) return true;
    return !!o.unlock(S, _entryHelpers);
}

const LOGBOOK_CATEGORIES = [
    { id: 'aliens',     name: 'ALIENS',     glyph: '♥', enabled: true },
    { id: 'bunnymeals', name: 'BUNNYMEALS', glyph: '◉', enabled: true },
    { id: 'items',      name: 'ITEMS',      glyph: '★', enabled: true },
    { id: 'locations',  name: 'LOCATIONS',  glyph: '◆', enabled: true },
    { id: 'characters', name: 'CHARACTERS', glyph: '✦', enabled: true },
    { id: 'events',     name: 'EVENTS',     glyph: '✿', enabled: true },
];

const RARITY_GLYPH = { common: '●', uncommon: '◆', rare: '★', epic: '★★', legendary: '★★★' };
const TIER_GLYPH = { 1: '★', 2: '◆', 3: '◇' };

let logbookCat = 'aliens';
let logbookSel = ALIEN_KEYS[0];
let logbookItemSel = TOY_KEYS[0] || null;
let logbookBunnymealSel = BUNNYMEAL_KEYS[0] || null;
let logbookCharSel = CHARACTER_KEYS[0] || null;

// ── Helpers ────────────────────────────────────────────────────────────

function _isDiscovered(alienId) {
    return (S.myAliens || []).some(a => a.alienId === alienId);
}

function _timesMet(alienId) {
    return (S.myAliens || []).filter(a => a.alienId === alienId).length;
}

function _ownsToy(toyId) {
    return (S.myToys || []).some(t => t.id === toyId);
}

function _toyCount(toyId) {
    return (S.myToys || []).find(t => t.id === toyId)?.count || 0;
}

// Unlock check for logbook characters. Reads Konmail's per-email state map
// (S.mailEmailStates); a character is unlocked once their triggering email
// has reached any state other than 'pending' (i.e. 'arrived', 'answered',
// 'expired', 'auto-penalised'). Konmail owns transitions — we just observe.
function _isCharUnlocked(c) {
    const emailId = CHARACTER_UNLOCK_EMAIL[c.id];
    if (!emailId) return true;
    const state = (S.mailEmailStates || {})[emailId];
    return !!state && state !== 'pending';
}

// ── Dossier entry resolver ─────────────────────────────────────────────
// Branching characters carry an `entries` array — typed notebook lines
// that filter by `when(S, h)` and sort chronologically by `day`. Sort is
// stable, so ties preserve source-array order (this is how the
// strike/correction visual pairing stays adjacent on the same day).
//
// Static characters (no `entries`) fall through to their flat `tags` +
// `desc` and render the legacy single-description layout, unchanged.
//
// kmStoryState and kmDateForDay are not exposed on window, so the two
// one-liners are replicated here (identical logic to konmail.js).
function _kmState(id)    { return (S.mailEmailStates || {})[id] || 'pending'; }
function _kmDate(day)    { return (window.KONMAIL_STORY_DAYS || {})[day] || null; }

// Helper bundle passed to each entry's `when(S, h)` callback. Mirrors the
// spec exactly so the data file reads naturally.
const _entryHelpers = {
    state:          (id)         => _kmState(id),
    delivered:      (id)         => _kmState(id) !== 'pending',
    answered:       (id)         => _kmState(id) === 'answered',
    // Mirrors kmCheckMatch's `unanswered:` predicate semantics (anything but
    // 'answered', including pending / expired / auto-penalised). `missed` is
    // stricter (only expired or auto-penalised).
    unanswered:     (id)         => _kmState(id) !== 'answered',
    choice:         (id, idx)    => (S.mailStoryChoices || {})[id] === idx,
    missed:         (id)         => { const s = _kmState(id); return s === 'expired' || s === 'auto-penalised'; },
    trust:          (char, lv)   => S['mailTrust' + char[0].toUpperCase() + char.slice(1)] === lv,
    records:        (n)          => (S.mailRecordsKept || 0) >= n,
    mystery:        (n)          => (S.mailMysteryDepth || 0) >= n,
    awareness:      (n)          => (S.mailClowAwareness || 0) >= n,
    awarenessExact: (n)          => (S.mailClowAwareness || 0) === n,
    shopHealth:     (n)          => (S.mailShopHealth || 0) >= n,
};

function _resolveCharEntries(c) {
    if (!c.entries || !c.entries.length) return [];
    const filtered = c.entries.filter(e => !e.when || e.when(S, _entryHelpers));
    // Stable sort by day ascending. V8/SpiderMonkey sort has been stable
    // since 2019, so ties keep their source order.
    const sorted = filtered.slice().sort((a, b) => (a.day || 0) - (b.day || 0));
    // Strike + correction pairing: per spec, a correction's refEntryId
    // points to the strike it visually follows, and the pair MUST render
    // adjacent regardless of source order. Pull each correction out of
    // line and re-insert it immediately after its referenced strike.
    const correctionsByRef = new Map();
    for (const e of sorted) {
        if (e.type === 'correction' && e.refEntryId) {
            correctionsByRef.set(e.refEntryId, e);
        }
    }
    if (correctionsByRef.size === 0) return sorted;
    const out = [];
    for (const e of sorted) {
        if (e.type === 'correction' && e.refEntryId) continue;
        out.push(e);
        const paired = correctionsByRef.get(e.id);
        if (paired) out.push(paired);
    }
    return out;
}

// Tag resolver — each tag is { text, when, struckWhen? }. A tag appears
// when `when(S, h)` is truthy; the renderer crosses it out (but keeps it
// visible) when `struckWhen(S, h)` is also truthy. This is how a note the
// dossier-keeper wrote down stays in the record even after a later truth
// contradicts it.
function _resolveCharTags(c) {
    if (!c.tags || !c.tags.length) return [];
    return c.tags
        .filter(t => {
            // Legacy texture-character tags are plain strings — always active.
            if (typeof t === 'string') return true;
            return !t.when || t.when(S, _entryHelpers);
        })
        .map(t => {
            if (typeof t === 'string') return { text: t, struck: false };
            return {
                text: t.text,
                struck: !!(t.struckWhen && t.struckWhen(S, _entryHelpers)),
            };
        });
}

// Parse the ISO date from kmDateForDay and emit display forms.
const _WD_TITLE = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'];
const _MO_TITLE = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
function _formatEntryDate(day) {
    const iso = _kmDate(day);
    if (!iso) return '';
    const d = new Date(iso + 'T00:00:00');
    return `${_WD_TITLE[d.getDay()]} · ${_MO_TITLE[d.getMonth()]} ${d.getDate()}`;
}
// "Tue May 19 · Day 2" — used in the §07 info boxes.
function _formatDateWithDay(day) {
    const iso = _kmDate(day);
    if (!iso) return '—';
    const d = new Date(iso + 'T00:00:00');
    return `${_WD_TITLE[d.getDay()]} ${_MO_TITLE[d.getMonth()]} ${d.getDate()} · Day ${day}`;
}

// Last Seen: walk the character's email IDs, return the latest day on
// which one of their emails moved off 'pending'. Returns null if none.
function _computeLastSeen(c) {
    if (!c.emailIds || !c.emailIds.length) return null;
    const days = {};
    for (const e of (window.KONMAIL_STORY || [])) days[e.id] = e.day;
    let max = null;
    for (const id of c.emailIds) {
        const st = (S.mailEmailStates || {})[id];
        if (st && st !== 'pending') {
            const d = days[id];
            if (d != null && (max == null || d > max)) max = d;
        }
    }
    return max;
}

// ── Navigation ─────────────────────────────────────────────────────────

function openLogbook() {
    openWin('w-logbook');
    if (!_isDiscovered(logbookSel)) {
        const firstDiscovered = ALIEN_KEYS.find(_isDiscovered);
        if (firstDiscovered) logbookSel = firstDiscovered;
    }
    renderLogbook();
}

function setLogbookCat(id) {
    if (!LOGBOOK_CATEGORIES.find(c => c.id === id)) return;
    logbookCat = id;
    renderLogbook();
}

function setLogbookSel(alienId) {
    if (!ALIENS[alienId]) return;
    logbookSel = alienId;
    renderLogbook();
}

function setLogbookItemSel(id) {
    const isToy = !!TOY_CATALOGUE.find(t => t.id === id);
    const isObj = _isObjectId(id);
    if (!isToy && !isObj) return;
    logbookItemSel = id;
    renderLogbook();
}

function logbookStep(dir) {
    let keys, cur;
    if (logbookCat === 'items')           { keys = ITEM_KEYS;      cur = logbookItemSel; }
    else if (logbookCat === 'bunnymeals') { keys = BUNNYMEAL_KEYS; cur = logbookBunnymealSel; }
    else if (logbookCat === 'characters') { keys = CHARACTER_KEYS; cur = logbookCharSel; }
    else                                  { keys = ALIEN_KEYS;     cur = logbookSel; }
    const n = keys.length;
    if (!n) return;
    const next = keys[(keys.indexOf(cur) + dir + n) % n];
    if (logbookCat === 'items')           logbookItemSel = next;
    else if (logbookCat === 'bunnymeals') logbookBunnymealSel = next;
    else if (logbookCat === 'characters') logbookCharSel = next;
    else                                  logbookSel = next;
    renderLogbook();
}

function setLogbookBunnymealSel(id) {
    if (!BUNNYMEALS[id]) return;
    logbookBunnymealSel = id;
    renderLogbook();
}

function setLogbookCharSel(id) {
    if (!LOGBOOK_CHARACTERS[id]) return;
    logbookCharSel = id;
    renderLogbook();
}

// ── Tab bar ────────────────────────────────────────────────────────────

function _tabsHTML() {
    return LOGBOOK_CATEGORIES.map(c => {
        const active = logbookCat === c.id;
        const cls = `lb-tab${active ? ' on cat-' + c.id : ''}`;
        let count;
        if (c.id === 'aliens') {
            const n = ALIEN_KEYS.filter(_isDiscovered).length;
            count = `<span class="lb-tab-count">${n}/${ALIEN_KEYS.length}</span>`;
        } else if (c.id === 'items') {
            const tn = TOY_CATALOGUE.filter(t => _ownsToy(t.id)).length;
            const on = OBJECT_KEYS.filter(id => _isObjectUnlocked(LOGBOOK_OBJECTS[id])).length;
            const total = TOY_CATALOGUE.length + OBJECT_KEYS.length;
            count = `<span class="lb-tab-count">${tn + on}/${total}</span>`;
        } else if (c.id === 'bunnymeals') {
            if (BUNNYMEAL_KEYS.length) {
                const n = BUNNYMEAL_KEYS.filter(id => (S.myBunnymeals || []).some(b => b.id === id)).length;
                count = `<span class="lb-tab-count">${n}/${BUNNYMEAL_KEYS.length}</span>`;
            } else {
                count = `<span class="lb-tab-count">SOON</span>`;
            }
        } else if (c.id === 'characters') {
            const n = CHARACTER_KEYS.filter(id => _isCharUnlocked(LOGBOOK_CHARACTERS[id])).length;
            count = `<span class="lb-tab-count">${n}/${CHARACTER_KEYS.length}</span>`;
        } else {
            count = `<span class="lb-tab-count">SOON</span>`;
        }
        return `<button class="${cls}" onclick="setLogbookCat('${c.id}')">
            <span class="lb-tab-glyph">${c.glyph}</span>
            <span class="lb-tab-name">${c.name}</span>
            ${count}
        </button>`;
    }).join('');
}

// ── Aliens list + detail ───────────────────────────────────────────────

function _listHTML() {
    return ALIEN_KEYS.map((id, i) => {
        const a = ALIENS[id];
        const owned = _isDiscovered(id);
        const sel = id === logbookSel;
        const idStr = `#${String(i + 1).padStart(3, '0')}`;
        const sprite = owned
            ? `<img src="${a.portrait || a.img}" alt="${a.name}" class="lb-li-sprite">`
            : `<div class="lb-li-sprite lb-silhouette"></div>`;
        return `<div class="lb-li${sel ? ' on' : ''}${owned ? '' : ' locked'} rarity-${a.rarity}" onclick="setLogbookSel('${id}')">
            <span class="lb-li-id">${idStr}</span>
            ${sprite}
            <span class="lb-li-name">${owned ? a.name : '??????'}</span>
            <span class="lb-li-rarity">${RARITY_GLYPH[a.rarity] || '●'}</span>
        </div>`;
    }).join('');
}

function _detailHTML() {
    const a = ALIENS[logbookSel];
    if (!a) return '';
    const idx = ALIEN_KEYS.indexOf(logbookSel);
    const idStr = `#${String(idx + 1).padStart(3, '0')}`;
    const owned = _isDiscovered(a.id);
    const times = _timesMet(a.id);
    if (!owned) {
        return `<div class="lb-detail">
            <div class="lb-detail-head">
                <div class="lb-portrait rarity-${a.rarity} locked">
                    <div class="lb-silhouette big"></div>
                </div>
                <div class="lb-detail-meta">
                    <span class="lb-detail-id">${idStr} · ALIEN</span>
                    <div class="lb-detail-name">??????</div>
                    <div class="lb-detail-tags">
                        <span class="lb-tag tag-rarity">${RARITY_GLYPH[a.rarity]} ???</span>
                    </div>
                    <p class="lb-detail-desc">A mystery awaits. Pull capsules and care for your aliens to discover this entry.</p>
                </div>
            </div>
            <div class="lb-meta-strip">
                <div class="lb-meta-cell"><span>FIRST SEEN</span><span>—</span></div>
                <div class="lb-meta-cell"><span>LOCATION</span><span>—</span></div>
                <div class="lb-meta-cell"><span>TIMES MET</span><span>× 0</span></div>
            </div>
            <div class="lb-detail-foot">
                <button class="lb-step" onclick="logbookStep(-1)">◀ PREV</button>
                <button class="lb-step" onclick="logbookStep(1)">NEXT ▶</button>
                <div class="lb-spacer"></div>
                <button class="lb-fav disabled" disabled>♥ FAVOURITE</button>
            </div>
        </div>`;
    }
    const isFav = (S.logbookFavorites || []).includes(a.id);
    const rarityTagClass = a.rarity === 'rare' ? 'tag-rare' : 'tag-rarity';
    const traitColors = ['tag-cyan', 'tag-lime'];
    const traitTags = (a.tags || []).map((t, i) =>
        `<span class="lb-tag ${traitColors[i] || 'tag-cyan'}">${t}</span>`
    ).join('');
    return `<div class="lb-detail">
        <div class="lb-detail-head">
            <div class="lb-portrait rarity-${a.rarity}">
                <img src="${a.portrait || a.img}" alt="${a.name}">
                <span class="lb-portrait-spark">✦</span>
            </div>
            <div class="lb-detail-meta">
                <span class="lb-detail-id">${idStr} · ALIEN</span>
                <div class="lb-detail-name">${a.name}</div>
                <div class="lb-detail-tags">
                    <span class="lb-tag ${rarityTagClass}">${RARITY_GLYPH[a.rarity]} ${a.rarity.toUpperCase()}</span>
                    ${traitTags}
                </div>
                <p class="lb-detail-desc">${a.desc}</p>
            </div>
        </div>
        <div class="lb-meta-strip">
            <div class="lb-meta-cell"><span>FIRST SEEN</span><span>${a.firstSeen || '???'}</span></div>
            <div class="lb-meta-cell"><span>LOCATION</span><span>KONBINI</span></div>
            <div class="lb-meta-cell"><span>TIMES MET</span><span>× ${times}</span></div>
        </div>
        <div class="lb-detail-foot">
            <button class="lb-step" onclick="logbookStep(-1)">◀ PREV</button>
            <button class="lb-step" onclick="logbookStep(1)">NEXT ▶</button>
            <div class="lb-spacer"></div>
            <button class="lb-fav${isFav ? ' active' : ''}" onclick="toggleLogbookFav('${a.id}')">
                ${isFav ? '★ FAVOURITED' : '☆ FAVOURITE'}
            </button>
        </div>
    </div>`;
}

// ── Items list + detail ────────────────────────────────────────────────

function _itemListHTML() {
    const toysHTML = TOY_CATALOGUE.map((toy, i) => {
        const owned = _ownsToy(toy.id);
        const sel = toy.id === logbookItemSel;
        const idStr = `#${String(i + 1).padStart(3, '0')}`;
        const sprite = owned
            ? `<img src="${toy.img}" alt="${toy.name}" class="lb-li-sprite">`
            : `<div class="lb-li-sprite lb-silhouette"></div>`;
        return `<div class="lb-li${sel ? ' on' : ''}${owned ? '' : ' locked'}" onclick="setLogbookItemSel('${toy.id}')">
            <span class="lb-li-id">${idStr}</span>
            ${sprite}
            <span class="lb-li-name">${owned ? toy.name : '??????'}</span>
            <span class="lb-li-rarity">★</span>
        </div>`;
    }).join('');
    const objectsHTML = OBJECT_KEYS.map((id, i) => {
        const o = LOGBOOK_OBJECTS[id];
        const unlocked = _isObjectUnlocked(o);
        const sel = id === logbookItemSel;
        const idStr = `#${String(TOY_KEYS.length + i + 1).padStart(3, '0')}`;
        const sprite = unlocked
            ? `<span class="lb-li-sprite lb-emoji-sprite">${o.emoji}</span>`
            : `<div class="lb-li-sprite lb-mystery-silhouette">?</div>`;
        return `<div class="lb-li${sel ? ' on' : ''}${unlocked ? '' : ' locked'}" onclick="setLogbookItemSel('${id}')">
            <span class="lb-li-id">${idStr}</span>
            ${sprite}
            <span class="lb-li-name">${unlocked ? o.name : '??????'}</span>
            <span class="lb-li-rarity">✦</span>
        </div>`;
    }).join('');
    return toysHTML + objectsHTML;
}

// Object detail — reuses the character dossier engine (entries + tags + day
// helpers). Renders a section header line so the visual separates from toys.
function _objectDetailHTML() {
    const o = LOGBOOK_OBJECTS[logbookItemSel];
    if (!o) return '<div class="lb-detail"></div>';
    const objIdx = OBJECT_KEYS.indexOf(o.id);
    const idStr = `#${String(TOY_KEYS.length + objIdx + 1).padStart(3, '0')}`;
    if (!_isObjectUnlocked(o)) {
        return `<div class="lb-detail dossier">
            <div class="lb-char-top">
                <div class="lb-char-portrait-frame is-mystery">
                    <span class="lb-mystery-glyph">?</span>
                </div>
                <div class="lb-char-header">
                    <div class="lb-char-id-line">
                        <span class="lb-char-num">${idStr}</span>
                        <span class="lb-char-type">NOT YET DISCOVERED</span>
                    </div>
                    <div class="lb-char-name lb-char-name-muted">??????</div>
                    <div class="lb-char-org-line">— · —</div>
                </div>
            </div>
            <div class="lb-char-locked-msg">
                <div class="lb-char-locked-title">Not yet discovered</div>
                <p class="lb-char-locked-flavor">no one has mentioned this yet.</p>
            </div>
            <div class="lb-char-footer">
                <button class="lb-char-btn lb-char-btn-teal" onclick="logbookStep(-1)">
                    <span class="lb-char-btn-arrow">◂</span> PREV
                </button>
                <button class="lb-char-btn lb-char-btn-pink" disabled>☆ FAVOURITE</button>
                <button class="lb-char-btn lb-char-btn-teal" onclick="logbookStep(1)">
                    NEXT <span class="lb-char-btn-arrow">▸</span>
                </button>
            </div>
        </div>`;
    }
    const entries = _resolveCharEntries(o);
    const tags = _resolveCharTags(o);
    const tagsHTML = tags.length
        ? `<div class="lb-char-tags">${tags.map(t =>
            `<span class="lb-char-tag${t.struck ? ' struck' : ''}">${t.text}</span>`
          ).join('')}</div>`
        : '';
    const firstContactValue = o.firstContactDay
        ? _formatDateWithDay(o.firstContactDay)
        : (o.firstContact || '—');
    return `<div class="lb-detail dossier">
        <div class="lb-char-top">
            <div class="lb-char-portrait-frame">
                <span class="lb-emoji-big">${o.emoji}</span>
            </div>
            <div class="lb-char-header">
                <div class="lb-char-id-line">
                    <span class="lb-char-num">${idStr}</span>
                    <span class="lb-char-type">${o.type || 'OBJECT'}</span>
                </div>
                <div class="lb-char-name">${o.name}</div>
                <div class="lb-char-org-line">${o.org || '—'}</div>
                ${tagsHTML}
            </div>
        </div>
        <div class="lb-char-info-row">
            <div class="lb-char-info-box">
                <div class="lb-char-info-label">First Mention</div>
                <div class="lb-char-info-value">${firstContactValue}</div>
            </div>
            <div class="lb-char-info-box">
                <div class="lb-char-info-label">Source</div>
                <div class="lb-char-info-value">${o.source || 'Unknown'}</div>
            </div>
            <div class="lb-char-info-box">
                <div class="lb-char-info-label">Status</div>
                <div class="lb-char-info-value">${_objectStatus(o)}</div>
            </div>
        </div>
        ${_renderEntriesHTML(entries)}
        <div class="lb-char-footer">
            <button class="lb-char-btn lb-char-btn-teal" onclick="logbookStep(-1)">
                <span class="lb-char-btn-arrow">◂</span> PREV
            </button>
            <button class="lb-char-btn lb-char-btn-pink" disabled>☆ FAVOURITE</button>
            <button class="lb-char-btn lb-char-btn-teal" onclick="logbookStep(1)">
                NEXT <span class="lb-char-btn-arrow">▸</span>
            </button>
        </div>
    </div>`;
}

// Short status string for the third info box. Drops uses its pulled-state;
// future objects can return a different short label.
function _objectStatus(o) {
    if (o.id === 'drops') {
        if (S.dropsPulledBy === 'player_beat1' || S.dropsPulledBy === 'player_beat2') return 'Pulled · you';
        if (S.dropsPulledBy === 'coneja') return 'Pulled · Coneja';
        if (S.packagePath === 'opened' && S.dropsPulled === false) return 'Out on shelf';
        if (S.packagePath === 'opened') return 'Pulled';
        if (S.packagePath === 'documented' || S.packagePath === 'left') return 'Sealed';
        return 'Rumoured';
    }
    if (o.id === 'sealed_package') {
        if (S.packagePath === 'opened')     return 'Opened';
        if (S.packagePath === 'documented') return 'Vanished';
        if (S.packagePath === 'left')       return 'Back room · sealed';
        return 'Sealed';
    }
    if (o.id === 'emorrow_letter') {
        if (S.documentedPath === true || S.mailEnding === 'documented') return 'Decoded';
        if (S.eMorrowSurfaced === true) return 'Surfaced to Clow';
        return 'Pending';
    }
    return '—';
}

function _itemDetailHTML() {
    if (_isObjectId(logbookItemSel)) return _objectDetailHTML();
    const toy = TOY_CATALOGUE.find(t => t.id === logbookItemSel);
    if (!toy) return '<div class="lb-detail"></div>';
    const owned = _ownsToy(toy.id);
    const count = _toyCount(toy.id);
    const idxStr = `#${String(TOY_KEYS.indexOf(toy.id) + 1).padStart(3, '0')}`;
    if (!owned) {
        return `<div class="lb-detail">
            <div class="lb-detail-head">
                <div class="lb-portrait locked">
                    <div class="lb-silhouette big"></div>
                </div>
                <div class="lb-detail-meta">
                    <span class="lb-detail-id">${idxStr} · ITEM</span>
                    <div class="lb-detail-name">??????</div>
                    <div class="lb-detail-tags">
                        <span class="lb-tag tag-rarity">★ ???</span>
                    </div>
                    <p class="lb-detail-desc">Pull from the capsule machine to discover this item!</p>
                </div>
            </div>
            <div class="lb-meta-strip">
                <div class="lb-meta-cell"><span>TYPE</span><span>—</span></div>
                <div class="lb-meta-cell"><span>SOURCE</span><span>—</span></div>
                <div class="lb-meta-cell"><span>OWNED</span><span>× 0</span></div>
            </div>
            <div class="lb-detail-foot">
                <button class="lb-step" onclick="logbookStep(-1)">◀ PREV</button>
                <button class="lb-step" onclick="logbookStep(1)">NEXT ▶</button>
                <div class="lb-spacer"></div>
            </div>
        </div>`;
    }
    return `<div class="lb-detail">
        <div class="lb-detail-head">
            <div class="lb-portrait">
                <img src="${toy.img}" alt="${toy.name}">
                <span class="lb-portrait-spark">★</span>
            </div>
            <div class="lb-detail-meta">
                <span class="lb-detail-id">${idxStr} · ITEM</span>
                <div class="lb-detail-name">${toy.name}</div>
                <div class="lb-detail-tags">
                    <span class="lb-tag tag-cyan">TOY</span>
                    <span class="lb-tag tag-lime">GACHA</span>
                </div>
                <p class="lb-detail-desc">${toy.desc}</p>
            </div>
        </div>
        <div class="lb-meta-strip">
            <div class="lb-meta-cell"><span>TYPE</span><span>ALIEN TOY</span></div>
            <div class="lb-meta-cell"><span>SOURCE</span><span>CAPSULE</span></div>
            <div class="lb-meta-cell"><span>OWNED</span><span>× ${count}</span></div>
        </div>
        <div class="lb-detail-foot">
            <button class="lb-step" onclick="logbookStep(-1)">◀ PREV</button>
            <button class="lb-step" onclick="logbookStep(1)">NEXT ▶</button>
            <div class="lb-spacer"></div>
        </div>
    </div>`;
}

// ── Bunnymeals list + detail ───────────────────────────────────────────

function _bunnymealListHTML() {
    if (!BUNNYMEAL_KEYS.length) {
        return `<div class="lb-list-items lb-list-soon">
            <span class="lb-soon-msg">✦ COMING SOON ✦</span>
        </div>`;
    }
    return BUNNYMEAL_KEYS.map((id, i) => {
        const bm = BUNNYMEALS[id];
        const owned = (S.myBunnymeals || []).some(b => b.id === id);
        const sel = id === logbookBunnymealSel;
        const idStr = `#${String(i + 1).padStart(3, '0')}`;
        const sprite = owned
            ? `<img src="${bm.img}" alt="${bm.name}" class="lb-li-sprite">`
            : `<div class="lb-li-sprite lb-silhouette"></div>`;
        return `<div class="lb-li${sel ? ' on' : ''}${owned ? '' : ' locked'} rarity-${bm.rarity}" onclick="setLogbookBunnymealSel('${id}')">
            <span class="lb-li-id">${idStr}</span>
            ${sprite}
            <span class="lb-li-name">${owned ? bm.name : '??????'}</span>
            <span class="lb-li-rarity">${RARITY_GLYPH[bm.rarity] || '●'}</span>
        </div>`;
    }).join('');
}

function _bunnymealDetailHTML() {
    if (!BUNNYMEAL_KEYS.length || !logbookBunnymealSel) {
        return `<div class="lb-detail"></div>`;
    }
    const bm = BUNNYMEALS[logbookBunnymealSel];
    if (!bm) return `<div class="lb-detail"></div>`;
    const idx = BUNNYMEAL_KEYS.indexOf(logbookBunnymealSel);
    const idStr = `#${String(idx + 1).padStart(3, '0')}`;
    const owned = (S.myBunnymeals || []).some(b => b.id === bm.id);
    if (!owned) {
        return `<div class="lb-detail">
            <div class="lb-detail-head">
                <div class="lb-portrait rarity-${bm.rarity} locked">
                    <div class="lb-silhouette big"></div>
                </div>
                <div class="lb-detail-meta">
                    <span class="lb-detail-id">${idStr} · BUNNYMEAL</span>
                    <div class="lb-detail-name">??????</div>
                    <div class="lb-detail-tags">
                        <span class="lb-tag tag-rarity">${RARITY_GLYPH[bm.rarity]} ???</span>
                    </div>
                    <p class="lb-detail-desc">A mystery awaits. Pull from the Bunnymeals banner to discover this entry.</p>
                </div>
            </div>
            <div class="lb-meta-strip">
                <div class="lb-meta-cell"><span>FIRST SEEN</span><span>—</span></div>
                <div class="lb-meta-cell"><span>LOCATION</span><span>—</span></div>
                <div class="lb-meta-cell"><span>TIMES MET</span><span>× 0</span></div>
            </div>
            <div class="lb-detail-foot">
                <button class="lb-step" onclick="logbookStep(-1)">◀ PREV</button>
                <button class="lb-step" onclick="logbookStep(1)">NEXT ▶</button>
                <div class="lb-spacer"></div>
            </div>
        </div>`;
    }
    const traitColors = ['tag-cyan', 'tag-lime'];
    const traitTags = (bm.tags || []).map((t, i) =>
        `<span class="lb-tag ${traitColors[i] || 'tag-cyan'}">${t}</span>`
    ).join('');
    return `<div class="lb-detail">
        <div class="lb-detail-head">
            <div class="lb-portrait rarity-${bm.rarity}">
                <img src="${bm.portrait || bm.img}" alt="${bm.name}">
                <span class="lb-portrait-spark">🍜</span>
            </div>
            <div class="lb-detail-meta">
                <span class="lb-detail-id">${idStr} · BUNNYMEAL</span>
                <div class="lb-detail-name">${bm.name}</div>
                <div class="lb-detail-tags">
                    <span class="lb-tag ${bm.rarity === 'rare' ? 'tag-rare' : 'tag-rarity'}">${RARITY_GLYPH[bm.rarity]} ${bm.rarity.toUpperCase()}</span>
                    ${traitTags}
                </div>
                <p class="lb-detail-desc">${bm.desc}</p>
            </div>
        </div>
        <div class="lb-meta-strip">
            <div class="lb-meta-cell"><span>FIRST SEEN</span><span>${bm.firstSeen || '???'}</span></div>
            <div class="lb-meta-cell"><span>LOCATION</span><span>KONBINI</span></div>
            <div class="lb-meta-cell"><span>COLLECTED</span><span>✓</span></div>
        </div>
        <div class="lb-detail-foot">
            <button class="lb-step" onclick="logbookStep(-1)">◀ PREV</button>
            <button class="lb-step" onclick="logbookStep(1)">NEXT ▶</button>
            <div class="lb-spacer"></div>
        </div>
    </div>`;
}

// ── Characters list + detail ───────────────────────────────────────────

// Global image-fallback helper: when an <img> 404s, swap it for an emoji
// span styled to fill the same slot. Lets us reference filenames before the
// PNG exists — drop the file and it just works on next reload.
function _lbCharImgFail(el, emoji, big) {
    const cls = big ? 'lb-emoji-big' : 'lb-li-sprite lb-emoji';
    el.outerHTML = `<span class="${cls}">${emoji}</span>`;
}
window._lbCharImgFail = _lbCharImgFail;

function _charListSprite(c) {
    if (c.tier === 3 || !c.filename) {
        return `<span class="lb-li-sprite lb-emoji">${c.emoji}</span>`;
    }
    return `<img src="assets/logbook/characters/${c.filename}" alt="${c.name}" class="lb-li-sprite" onerror="_lbCharImgFail(this,'${c.emoji}')">`;
}

function _charDetailPortrait(c) {
    // portraitImage (full path) takes precedence; falls back to legacy
    // `filename` (just the basename, with the conventional folder prefix).
    const src = c.portraitImage
        || (c.filename ? `assets/logbook/characters/${c.filename}` : null);
    if (c.tier === 3 || !src) {
        return `<span class="lb-emoji-big">${c.emoji}</span>`;
    }
    return `<img src="${src}" alt="${c.name}" onerror="_lbCharImgFail(this,'${c.emoji}',true)">`;
}

function _charListHTML() {
    return CHARACTER_KEYS.map((id, i) => {
        const c = LOGBOOK_CHARACTERS[id];
        const sel = id === logbookCharSel;
        const unlocked = _isCharUnlocked(c);
        const idStr = `#${String(i + 1).padStart(3, '0')}`;
        const sprite = unlocked
            ? _charListSprite(c)
            : `<div class="lb-li-sprite lb-mystery-silhouette">?</div>`;
        return `<div class="lb-li${sel ? ' on' : ''}${unlocked ? '' : ' locked'}" onclick="setLogbookCharSel('${id}')">
            <span class="lb-li-id">${idStr}</span>
            ${sprite}
            <span class="lb-li-name">${unlocked ? c.name : '??????'}</span>
            <span class="lb-li-rarity">${unlocked ? (TIER_GLYPH[c.tier] || '✦') : ''}</span>
        </div>`;
    }).join('');
}

function _charDetailHTML() {
    const c = LOGBOOK_CHARACTERS[logbookCharSel];
    if (!c) return '<div class="lb-detail"></div>';
    const idStr = `#${String(CHARACTER_KEYS.indexOf(c.id) + 1).padStart(3, '0')}`;
    if (!_isCharUnlocked(c)) {
        // Locked: §07 chrome with placeholder content. No tags, no info
        // boxes, no dossier — just a centered "Not yet contacted" card
        // and a footer (FAVOURITE disabled — they're not really known).
        return `<div class="lb-detail dossier">
            <div class="lb-char-top">
                <div class="lb-char-portrait-frame is-mystery">
                    <span class="lb-mystery-glyph">?</span>
                </div>
                <div class="lb-char-header">
                    <div class="lb-char-id-line">
                        <span class="lb-char-num">${idStr}</span>
                        <span class="lb-char-type">NOT YET CONTACTED</span>
                    </div>
                    <div class="lb-char-name lb-char-name-muted">??????</div>
                    <div class="lb-char-org-line">— · —</div>
                </div>
            </div>
            <div class="lb-char-locked-msg">
                <div class="lb-char-locked-title">Not yet contacted</div>
                <p class="lb-char-locked-flavor">this character hasn't written in yet.</p>
            </div>
            <div class="lb-char-footer">
                <button class="lb-char-btn lb-char-btn-teal" onclick="logbookStep(-1)">
                    <span class="lb-char-btn-arrow">◂</span> PREV
                </button>
                <button class="lb-char-btn lb-char-btn-pink" disabled>
                    ☆ FAVOURITE
                </button>
                <button class="lb-char-btn lb-char-btn-teal" onclick="logbookStep(1)">
                    NEXT <span class="lb-char-btn-arrow">▸</span>
                </button>
            </div>
        </div>`;
    }
    // Unified §07 character-page layout for ALL unlocked characters.
    // Texture characters (no entries[]) get an empty dossier section that
    // doesn't render at all — header + tags + records + info boxes + footer
    // only. The legacy `c.desc` paragraph is intentionally not rendered;
    // it's a pre-dossier artifact that frequently contains spoilers (e.g.
    // Coneja's "Found the package on Day 6" visible before Day 6).
    const entries = _resolveCharEntries(c);
    const tags = _resolveCharTags(c);
    const tagsHTML = tags.length
        ? `<div class="lb-char-tags">${tags.map(t =>
            `<span class="lb-char-tag${t.struck ? ' struck' : ''}">${t.text}</span>`
          ).join('')}</div>`
        : '';
    const lastSeenDay = _computeLastSeen(c);
    const firstContactValue = c.firstContactDay
        ? _formatDateWithDay(c.firstContactDay)
        : (c.firstContact || '—');
    const lastSeenValue = (lastSeenDay != null)
        ? _formatDateWithDay(lastSeenDay)
        : '—';
    const isFav = (S.logbookFavorites || []).includes(c.id);
    return `<div class="lb-detail dossier">
        <div class="lb-char-top">
            <div class="lb-char-portrait-frame">
                ${_charDetailPortrait(c)}
            </div>
            <div class="lb-char-header">
                <div class="lb-char-id-line">
                    <span class="lb-char-num">${idStr}</span>
                    <span class="lb-char-type">CHARACTER · TIER ${c.tier}</span>
                </div>
                <div class="lb-char-name">${c.name}</div>
                <div class="lb-char-org-line">${c.org}</div>
                ${tagsHTML}
                ${_recordsPillHTML()}
            </div>
        </div>
        <div class="lb-char-info-row">
            <div class="lb-char-info-box">
                <div class="lb-char-info-label">First Contact</div>
                <div class="lb-char-info-value">${firstContactValue}</div>
            </div>
            <div class="lb-char-info-box">
                <div class="lb-char-info-label">Emails</div>
                <div class="lb-char-info-value">${c.emails || '—'}</div>
            </div>
            <div class="lb-char-info-box">
                <div class="lb-char-info-label">Last Seen</div>
                <div class="lb-char-info-value">${lastSeenValue}</div>
            </div>
        </div>
        ${_renderEntriesHTML(entries)}
        <div class="lb-char-footer">
            <button class="lb-char-btn lb-char-btn-teal" onclick="logbookStep(-1)">
                <span class="lb-char-btn-arrow">◂</span> PREV
            </button>
            <button class="lb-char-btn lb-char-btn-pink${isFav ? ' active' : ''}" onclick="toggleLogbookFav('${c.id}')">
                ${isFav ? '★ FAVOURITED' : '☆ FAVOURITE'}
            </button>
            <button class="lb-char-btn lb-char-btn-teal" onclick="logbookStep(1)">
                NEXT <span class="lb-char-btn-arrow">▸</span>
            </button>
        </div>
    </div>`;
}

// ── Entry renderer ─────────────────────────────────────────────────────
function _renderEntry(e) {
    const type = e.type || 'note';
    const dateStamp = _formatEntryDate(e.day);
    const text = e.text || '';
    let body;
    if (type === 'quote') {
        const attrib = e.attrib ? `<span class="lb-entry-attrib">— ${e.attrib}</span>` : '';
        body = `<span class="lb-entry-text">${text}</span>${attrib}`;
    } else if (type === 'missed') {
        const closedOn = dateStamp || (e.day ? `Day ${e.day}` : '');
        body = `<span class="lb-entry-missed-pill">window closed — ${closedOn}</span><span class="lb-entry-text">${text}</span>`;
    } else {
        body = `<span class="lb-entry-text">${text}</span>`;
    }
    const refAttr = e.refEntryId ? ` data-ref="${e.refEntryId}"` : '';
    return `<div class="lb-entry" data-type="${type}" data-entry="${e.id || ''}"${refAttr}>
        <span class="lb-entry-date">${dateStamp}</span>
        <div class="lb-entry-body">${body}</div>
    </div>`;
}
function _renderEntriesHTML(entries) {
    if (!entries.length) return '';
    return `<div class="lb-entries">${entries.map(_renderEntry).join('')}</div>`;
}

// ── Records Kept pill (one-shot pulse on 4 → 5 transition) ─────────────
let _prevRecordsKept = (S && (S.mailRecordsKept || 0)) || 0;
function _recordsPillHTML() {
    const n = S.mailRecordsKept || 0;
    const justHitFive = (n === 5 && _prevRecordsKept !== 5);
    _prevRecordsKept = n;
    const cls = ['lb-records-pill'];
    if (n === 5) cls.push('complete');
    if (justHitFive) cls.push('pulse');
    return `<span class="${cls.join(' ')}">✦ records kept: ${n}</span>`;
}

// ── Coming-soon placeholder ────────────────────────────────────────────

function _comingSoonHTML(catName, catGlyph) {
    return `<div class="lb-detail">
        <div class="lb-detail-head">
            <div class="lb-portrait" style="background:var(--lb-peach);">
                <span style="font-size:42px;line-height:1;">${catGlyph}</span>
            </div>
            <div class="lb-detail-meta">
                <div class="lb-detail-name">${catName}</div>
                <div class="lb-detail-tags">
                    <span class="lb-tag tag-cyan">COMING SOON</span>
                </div>
                <p class="lb-detail-desc">These entries are on their way! New pages will be added as the konbini grows and new adventures unfold.</p>
            </div>
        </div>
    </div>`;
}

// ── Main render ────────────────────────────────────────────────────────

function _splitHTML() {
    if (logbookCat === 'aliens') {
        const disc = ALIEN_KEYS.filter(_isDiscovered).length;
        return `<div class="lb-list">
            <div class="lb-list-head">
                <span class="lb-list-title">♥ ALIENS · ${disc}/${ALIEN_KEYS.length}</span>
                <span class="lb-list-sort">SORT: ID</span>
            </div>
            <div class="lb-list-items">${_listHTML()}</div>
        </div>
        ${_detailHTML()}`;
    }
    if (logbookCat === 'items') {
        const toyDisc = TOY_CATALOGUE.filter(t => _ownsToy(t.id)).length;
        const objDisc = OBJECT_KEYS.filter(id => _isObjectUnlocked(LOGBOOK_OBJECTS[id])).length;
        const totalDisc  = toyDisc + objDisc;
        const totalCount = TOY_CATALOGUE.length + OBJECT_KEYS.length;
        return `<div class="lb-list">
            <div class="lb-list-head">
                <span class="lb-list-title">★ ITEMS · ${totalDisc}/${totalCount}</span>
                <span class="lb-list-sort">SORT: ID</span>
            </div>
            <div class="lb-list-items">${_itemListHTML()}</div>
        </div>
        ${_itemDetailHTML()}`;
    }
    if (logbookCat === 'characters') {
        const disc = CHARACTER_KEYS.filter(id => _isCharUnlocked(LOGBOOK_CHARACTERS[id])).length;
        return `<div class="lb-list">
            <div class="lb-list-head">
                <span class="lb-list-title">✦ CHARACTERS · ${disc}/${CHARACTER_KEYS.length}</span>
                <span class="lb-list-sort">SORT: ID</span>
            </div>
            <div class="lb-list-items">${_charListHTML()}</div>
        </div>
        ${_charDetailHTML()}`;
    }
    if (logbookCat === 'bunnymeals') {
        const disc = BUNNYMEAL_KEYS.filter(id => (S.myBunnymeals || []).some(b => b.id === id)).length;
        const total = BUNNYMEAL_KEYS.length;
        const header = total
            ? `<span class="lb-list-title">◉ BUNNYMEALS · ${disc}/${total}</span><span class="lb-list-sort">SORT: ID</span>`
            : `<span class="lb-list-title">◉ BUNNYMEALS</span>`;
        return `<div class="lb-list">
            <div class="lb-list-head">${header}</div>
            <div class="lb-list-items">${_bunnymealListHTML()}</div>
        </div>
        ${total ? _bunnymealDetailHTML() : _comingSoonHTML('BUNNYMEALS', '◉')}`;
    }
    const cat = LOGBOOK_CATEGORIES.find(c => c.id === logbookCat);
    const catName = cat ? cat.name : logbookCat.toUpperCase();
    const catGlyph = cat ? cat.glyph : '✦';
    return `<div class="lb-list">
        <div class="lb-list-head">
            <span class="lb-list-title">${catGlyph} ${catName}</span>
        </div>
        <div class="lb-list-items lb-list-soon">
            <span class="lb-soon-msg">✦ COMING SOON ✦</span>
        </div>
    </div>
    ${_comingSoonHTML(catName, catGlyph)}`;
}

function renderLogbook() {
    const root = document.getElementById('logbook-content');
    if (!root) return;
    root.innerHTML = `
        <div class="lb-tabs">${_tabsHTML()}</div>
        <div class="lb-split">${_splitHTML()}</div>`;
}

// ── Favourite toggle ───────────────────────────────────────────────────

function toggleLogbookFav(id) {
    if (!S.logbookFavorites) S.logbookFavorites = [];
    if (S.logbookFavorites.includes(id)) {
        S.logbookFavorites = S.logbookFavorites.filter(x => x !== id);
        toast('Removed from favourites!');
    } else {
        S.logbookFavorites.push(id);
        SFX.play('unlock');
        toast('Favourited! ★');
        if (window.triggerMission) window.triggerMission('logbook_fav');
    }
    scheduleSave();
    renderLogbook();
}

window.openLogbook             = openLogbook;
window.renderLogbook           = renderLogbook;
window.setLogbookCat           = setLogbookCat;
window.setLogbookSel           = setLogbookSel;
window.setLogbookItemSel       = setLogbookItemSel;
window.setLogbookBunnymealSel  = setLogbookBunnymealSel;
window.setLogbookCharSel       = setLogbookCharSel;
window.logbookStep             = logbookStep;
window.toggleLogbookFav        = toggleLogbookFav;
